// src/components/PaperPositionsView.js
import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
  Paper,
} from "@mui/material";
import { closePaperTrade, listPaperPositions, getPaperPnL } from "../api/paperTrade";
import ResponsiveContainer from "./ResponsiveContainer";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtUsd(val) {
  if (val == null) return "—";
  const n = Number(val);
  return (n >= 0 ? "+" : "") + n.toFixed(2);
}

function pnlColor(val) {
  if (val == null) return "default";
  return Number(val) >= 0 ? "success" : "error";
}

function scoreColor(score) {
  const n = Number(score ?? 0);
  if (n >= 60) return "success";
  if (n >= 40) return "warning";
  return "default";
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PaperPositionsView = () => {
  const [positions, setPositions] = useState([]);
  const [tab, setTab] = useState("open");   // "open" | "closed" | "all"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [closingId, setClosingId] = useState(null);   // trade id being closed
  const [exitPriceInput, setExitPriceInput] = useState("");
  const [closeError, setCloseError] = useState("");
  const [pnlData, setPnlData] = useState({});   // Map of trade id ? { unrealised_pnl_usd, unrealised_pnl_pct, current_price }
  const [pnlLoading, setPnlLoading] = useState(false);

  const fetchPositions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const filter = tab === "all" ? undefined : tab;
      const data = await listPaperPositions(filter);
      setPositions(data?.positions ?? []);
    } catch {
      setError("Failed to load positions.");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    fetchPositions();
  }, [fetchPositions]);

  const handleTabChange = (_e, val) => {
    setTab(val);
    setClosingId(null);
    setExitPriceInput("");
    setCloseError("");
  };

  const handleCloseClick = (id) => {
    setClosingId(id);
    setExitPriceInput("");
    setCloseError("");
  };

  const handleCloseConfirm = async (id) => {
    const price = parseFloat(exitPriceInput);
    if (!price || price <= 0) {
      setCloseError("Enter a valid exit price > 0");
      return;
    }
    try {
      await closePaperTrade(id, price);
      setClosingId(null);
      setExitPriceInput("");
      fetchPositions();
    } catch {
      setCloseError("Failed to close trade. Please try again.");
    }
  };

  const handleUpdatePnL = async () => {
    setPnlLoading(true);
    try {
      const data = await getPaperPnL();
      const pnlMap = {};
      (data?.pnl ?? []).forEach((item) => {
        pnlMap[item.id] = {
          unrealised_pnl_usd: item.unrealised_pnl_usd,
          unrealised_pnl_pct: item.unrealised_pnl_pct,
          current_price: item.current_price,
        };
      });
      setPnlData(pnlMap);
    } catch {
      // silently fail
    } finally {
      setPnlLoading(false);
    }
  };

  // Summary stats for visible positions
  const openPositions = positions.filter((p) => p.status === "open");
  const closedPositions = positions.filter((p) => p.status === "closed");
  const totalOpenNotional = openPositions.reduce((s, p) => s + Number(p.notional_usd ?? 0), 0);
  const totalRealisedPnl = closedPositions.reduce((s, p) => s + Number(p.realised_pnl_usd ?? 0), 0);

  return (
    <ResponsiveContainer>
      <Typography variant="h5" gutterBottom>
        Paper Positions
      </Typography>

      {/* Summary cards */}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} mb={2}>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent sx={{ py: 1 }}>
            <Typography variant="caption" color="text.secondary">Open positions</Typography>
            <Typography variant="h6">{openPositions.length}</Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent sx={{ py: 1 }}>
            <Typography variant="caption" color="text.secondary">Open notional (USD)</Typography>
            <Typography variant="h6">${totalOpenNotional.toFixed(2)}</Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent sx={{ py: 1 }}>
            <Typography variant="caption" color="text.secondary">Realised P&amp;L (closed)</Typography>
            <Typography
              variant="h6"
              color={totalRealisedPnl >= 0 ? "success.main" : "error.main"}
            >
              {fmtUsd(totalRealisedPnl)}
            </Typography>
          </CardContent>
        </Card>
        <Card variant="outlined" sx={{ flex: 1 }}>
          <CardContent sx={{ py: 1 }}>
            <Typography variant="caption" color="text.secondary">Closed trades</Typography>
            <Typography variant="h6">{closedPositions.length}</Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Status filter tabs */}
      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 1 }}>
        <Tab label="Open" value="open" />
        <Tab label="Closed" value="closed" />
        <Tab label="All" value="all" />
      </Tabs>

      <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
        <Button size="small" variant="outlined" onClick={fetchPositions}>
          Refresh
        </Button>
        <Button size="small" variant="outlined" onClick={handleUpdatePnL} disabled={pnlLoading}>
          {pnlLoading ? "Updating..." : "Update P&L"}
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {closeError && <Alert severity="warning" sx={{ mb: 1 }}>{closeError}</Alert>}

      {!loading && positions.length === 0 && (
        <Typography color="text.secondary">No {tab === "all" ? "" : tab} positions found.</Typography>
      )}

      {positions.length > 0 && (
        <Box sx={{ width: "100%", overflowX: "auto" }}>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell>Symbol</TableCell>
                  <TableCell>Entry Price</TableCell>
                  <TableCell>Shares</TableCell>
                  <TableCell>Notional</TableCell>
                  <TableCell>Score</TableCell>
                  <TableCell>Size %</TableCell>
                  <TableCell>Entry Time</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Current Price</TableCell>
                  <TableCell>Unrealised P&amp;L</TableCell>
                  <TableCell>Exit Price</TableCell>
                  <TableCell>Realised P&amp;L</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {positions.map((pos) => (
                  <TableRow key={pos.id} hover>
                    <TableCell>
                      <Typography fontWeight={600}>{pos.symbol}</Typography>
                    </TableCell>
                    <TableCell>${Number(pos.entry_price).toFixed(2)}</TableCell>
                    <TableCell>{Number(pos.shares).toFixed(4)}</TableCell>
                    <TableCell>${Number(pos.notional_usd).toFixed(2)}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        variant="outlined"
                        color={scoreColor(pos.composite_score)}
                        label={Number(pos.composite_score).toFixed(1)}
                      />
                    </TableCell>
                    <TableCell>{pos.position_size_pct}%</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {pos.entry_time ? new Date(pos.entry_time).toLocaleString() : "—"}
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={pos.status}
                        color={pos.status === "open" ? "info" : "default"}
                        variant={pos.status === "open" ? "filled" : "outlined"}
                      />
                    </TableCell>
                    <TableCell>
                      {pos.status === "open" && pnlData[pos.id]?.current_price != null
                        ? `$${Number(pnlData[pos.id].current_price).toFixed(2)}`
                        : "-"}
                    </TableCell>
                    <TableCell>
                      {pos.status === "open" && pnlData[pos.id]?.unrealised_pnl_usd != null ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={pnlColor(pnlData[pos.id].unrealised_pnl_usd)}
                          label={`${fmtUsd(pnlData[pos.id].unrealised_pnl_usd)} (${Number(pnlData[pos.id].unrealised_pnl_pct ?? 0).toFixed(1)}%)`}
                        />
                      ) : "-"}
                    </TableCell>
                    <TableCell>
                      {pos.exit_price != null ? `$${Number(pos.exit_price).toFixed(2)}` : "—"}
                    </TableCell>
                    <TableCell>
                      {pos.realised_pnl_usd != null ? (
                        <Chip
                          size="small"
                          variant="outlined"
                          color={pnlColor(pos.realised_pnl_usd)}
                          label={fmtUsd(pos.realised_pnl_usd)}
                        />
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      {pos.status === "open" && (
                        closingId === pos.id ? (
                          <Stack direction="row" spacing={0.5} sx={{ alignItems: "center" }}>
                            <TextField
                              size="small"
                              placeholder="Exit $"
                              value={exitPriceInput}
                              onChange={(e) => setExitPriceInput(e.target.value)}
                              slotProps={{
                                htmlInput: { "aria-label": `exit price for ${pos.symbol}` },
                              }}
                              sx={{ width: 90 }}
                            />
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              onClick={() => handleCloseConfirm(pos.id)}
                              aria-label={`confirm close ${pos.symbol}`}
                            >
                              Confirm
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => { setClosingId(null); setCloseError(""); }}
                            >
                              Cancel
                            </Button>
                          </Stack>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleCloseClick(pos.id)}
                            aria-label={`close position ${pos.symbol}`}
                          >
                            Close
                          </Button>
                        )
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </ResponsiveContainer>
  );
};

export default PaperPositionsView;






